/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    GuideApi,
    GuideRowsGetRequest,
} from 'client-code/grid-guide';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types/TServicesParams';
import { IGridGuideServiceRepository } from '../interfaces/IGridGuideServiceRepository';
import { GridGuideRow } from '../entities/GridGuideRow';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { Constants } from '../constants';
import { CommonHeaders } from '../CommonHeaders';
import { DeviceModel } from '../device/DeviceModel';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class GridGuideRepositoryImpl implements IGridGuideServiceRepository {
    mParams: TServicesParams;

    endpointVersion?: string;

    TAG: string = 'GridGuideRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    async fetchProgramMetadata(
        stationId: string[],
        startTime: Date,
        endTime: Date,
    ): Promise<GridGuideRow[]> {
        try {
            if (startTime.getMinutes() % 30) {
                throw new Error('Start Time should be in nearest half hour');
            }

            if (endTime.getMinutes() % 30) {
                throw new Error('End Time should be in nearest half hour');
            }
            const startTimeInSeconds: number = Math.floor(startTime.getTime() / 1000);
            const endTimeInSeconds: number = Math.floor(endTime.getTime() / 1000);

            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;

            const config: ConfigurationParameters = {
                basePath: this.mParams.basePath,
                apiKey: this.mParams.accessToken,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            // Sorting the stationIds lexographically and
            // returning in the same order as requested by UI
            const uniqueSortedStationIds = stationId
                .filter((id, index) => stationId.indexOf(id) === index)
                .sort();

            const requestParameters: GuideRowsGetRequest = {
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                bodyId: this.mParams.bodyId,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                stationId: uniqueSortedStationIds,
                windowStartTime: startTimeInSeconds,
                windowEndTime: endTimeInSeconds,
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                deviceType: commonHeaders.DeviceType,
                productName: commonHeaders.ProductName,
            };

            const requestConfiguration = new Configuration(config);
            const gridGuide = new GuideApi(requestConfiguration);
            const gridGuideResponse = await gridGuide.guideRowsGet(requestParameters);

            const gridGuideResponseMap = new Map();
            gridGuideResponse.data.forEach((guideRow) => {
                gridGuideResponseMap.set(
                    guideRow.stationId,
                    new GridGuideRow(guideRow, this.mParams.imageHostBaseUrl),
                );
            });

            const gridGuideUiResponse: Array<GridGuideRow> = [];
            stationId.forEach((id) => {
                gridGuideUiResponse.push(gridGuideResponseMap.get(id));
            });

            return gridGuideUiResponse;
        } catch (error: any) {
            Log.e(this.TAG, 'fetchProgramMetadata()', error.message, {
                stationId,
                widowStartTime: startTime,
                windowEndTime: endTime,
            });
            throw error;
        }
    }
}
